import { useEffect, useMemo, useState } from 'react'
import { NavigationControl } from 'react-map-gl/mapbox'
import { DEFAULT_MAP_FILTERS, DEFAULT_MAP_SETTINGS, MAP_IMAGES, MAP_STYLE_OPTIONS } from './constants'
import { PointsOfInterestLayer } from './components/Layers/PointsOfInterest'
import { DriverMapMarker } from './components/DriverMapMarker'
import { OrderRouteLayer } from './components/Layers/OrderRouteLayer'
import { CurrentPositionMarker } from './components/CurrentPositionMarker'
import {
  createRoutes,
  filterPointsOfInterest,
  getActionPinsGeoJSON,
  getDriverPopoverInfo,
  getPointsOfInterestOnRoute,
} from './util'
import { MapPopover } from './components/MapPopover'
import type { DriverSchedule, PointsOfInterestResponse } from '../../types'
import type { calculateOptimalViewState, getInitialDriverViewState } from '../../util'
import { useUserSettings } from '@/hooks/settings'
import { MapStyleToggle } from './components/MapStyleToggle'
import type { MapFilters, MapSettings, MapStyle } from './types'
import type { MapboxMapHook } from '@/components/_SiteWide/MapboxMap/hooks/useMapboxMap'
import { MapboxMap } from '@/components/_SiteWide/MapboxMap'
import { useDriverTrackingState } from '../../stores'
import { MapHeader } from './components/MapHeader'
import { useThemeContext, Vertical } from '@gravitate-js/excalibrr'
import { ConfigSpecification } from 'mapbox-gl'
import { usePoiLayers } from './components/usePoiLayers'
import { useRouteLayers } from './components/useRouteLayers'

const SETTINGS_KEY = 'driver-tracking::map-settings'

type DriverMapProps = {
  mapboxMapHook: MapboxMapHook
  allDriverTracking: DriverSchedule[]
  pointsOfInterest: PointsOfInterestResponse
  driverSchedule: DriverSchedule | null
  selectedDriverId: string | null
  handleDriverSelect: (driverId: string) => void
  initialViewState: ReturnType<typeof getInitialDriverViewState>
}

export function DriverMap({
  mapboxMapHook,
  allDriverTracking,
  pointsOfInterest,
  driverSchedule,
  selectedDriverId,
  handleDriverSelect,
  initialViewState,
}: DriverMapProps) {
  const {
    mapRef,
    popoverInfo,
    mapSettings,
    gravitateMapStyleURL,
    setPopoverInfo,
    flyToLocation,
    handleMapStyleChange,
    handleUpdateMapSettings,
    setIsStyleLoaded,
  } = mapboxMapHook
  const { driverOrdersOnRoute } = useDriverTrackingState()
  const [mapFilters, setMapFilters] = useState<MapFilters>(DEFAULT_MAP_FILTERS)

  const pointsOfInterestOnRoute = useMemo(
    () => getPointsOfInterestOnRoute(selectedDriverId, driverSchedule, allDriverTracking, pointsOfInterest),
    [driverSchedule?.route, pointsOfInterest, selectedDriverId, allDriverTracking]
  )

  const filteredPointsOfInterest = useMemo(
    () =>
      filterPointsOfInterest(
        pointsOfInterest,
        pointsOfInterestOnRoute,
        mapFilters,
        mapSettings?.showLocations || 'all'
      ),
    [pointsOfInterest, mapFilters, pointsOfInterestOnRoute, mapSettings?.showLocations]
  )

  const driverRoutes = useMemo(
    () => createRoutes(driverSchedule?.breadcrumbs, driverOrdersOnRoute, driverSchedule?.timezone),
    [driverSchedule?.breadcrumbs, driverOrdersOnRoute]
  )
  const actionPins = useMemo(
    () => getActionPinsGeoJSON(driverSchedule?.route, driverSchedule?.timezone),
    [driverSchedule?.route]
  )

  function handleFilterChange<T extends keyof MapFilters>(
    poiType: T,
    filterKey: keyof MapFilters[T],
    value: MapFilters[T][keyof MapFilters[T]]
  ) {
    setMapFilters((prev) => {
      const newFilters = { ...prev }
      newFilters[poiType][filterKey] = value
      return newFilters
    })
  }

  return (
    <Vertical width={'100%'}>
      <MapboxMap
        ref={mapRef}
        initialViewState={initialViewState}
        mapImages={MAP_IMAGES}
        mapStyle={gravitateMapStyleURL}
        setIsStyleLoaded={setIsStyleLoaded}
      >
        <MapHeader
          pointsOfInterest={mapSettings?.showLocations === 'only_on_route' ? pointsOfInterestOnRoute : pointsOfInterest}
          filters={mapFilters}
          settings={mapSettings}
          handleFilterChange={handleFilterChange}
          handleLocationSearch={flyToLocation}
          handleUpdateMapSettings={handleUpdateMapSettings}
        />
        <MapStyleToggle currentStyle={mapSettings?.style} handleMapStyle={handleMapStyleChange} />
        <PointsOfInterestLayer
          mapboxMapHook={mapboxMapHook}
          pointsOfInterest={filteredPointsOfInterest}
          hoveredId={popoverInfo?.id}
        />
        {selectedDriverId === null ? (
          allDriverTracking?.map((driver, index) => (
            <DriverMapMarker
              key={driver.driver_schedule_id}
              driverInfo={getDriverPopoverInfo(
                driver.last_update,
                driver.driver_schedule_id,
                driver.driver_name,
                driver.complete,
                driver.timezone
              )}
              driverId={driver.driver_id}
              handleDriverSelect={handleDriverSelect}
              setPopoverInfo={setPopoverInfo}
            />
          ))
        ) : (
          <>
            {Object.entries(driverOrdersOnRoute)
              .filter(([_, routeProperties]) => routeProperties.isVisible)
              .map(([orderNumber]) => (
                <OrderRouteLayer
                  mapboxMapHook={mapboxMapHook}
                  key={orderNumber}
                  id={orderNumber}
                  breadcrumbRoute={driverRoutes[parseInt(orderNumber)]}
                  actionPins={actionPins[orderNumber]}
                  showBreadcrumbs={mapSettings?.showBreadcrumbs}
                />
              ))}
            <CurrentPositionMarker
              lastHeartbeat={driverSchedule?.last_update?.last_heartbeat}
              lat={driverSchedule?.last_update?.lat}
              lon={driverSchedule?.last_update?.lon}
              complete={driverSchedule?.complete ?? false}
            />
          </>
        )}
        <MapPopover popoverInfo={popoverInfo} mapRef={mapRef} />
      </MapboxMap>
    </Vertical>
  )
}
